import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { DefaultDateField, SelectField, MultiSelectField } from "../../forms"
import Card from "../../card"
import Grid from "../../grid"
import { P } from "../../typography"
import Button from "../../button"
import CloseIcon from "../../close-icon"
import { Container, StyledFieldGroup, StyledLabel } from "./styles"
import NumberField from "../../forms/number-field"

const Filters = ({ filters, filterHandler, q, clearFilterHandler, clearAllFiltersHandler }) => {
  const [showFilter, setShowFilter] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState({})
  const [dateHolder, setDateHolder] = useState({
    value: "",
    name: ""
  })
  const [numberHolder, setNumberHolder] = useState({
    value: "",
    name: ""
  })
  const [multiValue, setMultiValue] = useState([])

  const handleFilterChange = (name, value) => {
    if (filterHandler !== undefined) filterHandler(name, value)
    setShowFilter(false)
    setSelectedFilter({})
  }

  const clearAllFilters = () => {
    if (clearAllFiltersHandler !== undefined) clearAllFiltersHandler()
  }

  const handleClearFilter = (name) => {
    if (clearFilterHandler !== undefined) clearFilterHandler(name)
    setShowFilter(false)
    setSelectedFilter({})
    setDateHolder({ value: "", name: "" })
    setNumberHolder({ value: "", name: "" })
    setNumberHolder({ value: "", name: "" })
    setMultiValue([])
  }

  const toggleFilter = (event, filter) => {
    if (filter.type === "boolean") {
      if (filterHandler === undefined) filterHandler(event, filter.param, !checkSelected(filter))
    } else if (filter.param === selectedFilter.param) {
      setShowFilter(false)
      setSelectedFilter({})
    } else {
      setSelectedFilter(filter)
      setShowFilter(true)
    }
  }

  const handleDateFieldChange = (name, value) => {
    setDateHolder({ value, name })
  }

  const handleNumberFieldChange = (name, value) => {
    setNumberHolder({ value, name })
  }

  const saveDateChange = (name) => {
    if (filterHandler !== undefined) filterHandler(name, dateHolder.value)
    setShowFilter(false)
    setSelectedFilter({})
    setDateHolder({ name: "", value: "" })
  }

  const saveNumberChange = (name) => {
    if (filterHandler !== undefined) filterHandler(name, numberHolder.value)
    setShowFilter(false)
    setSelectedFilter({})
    setNumberHolder({ name: "", value: "" })
  }

  const handleMultiSelectChange = (value) => {
    setMultiValue(value)
  }

  const saveMultiSelectChange = (name) => {
    if (filterHandler !== undefined) filterHandler(name, multiValue)
    setShowFilter(false)
    setSelectedFilter({})
    setMultiValue([])
  }

  const isDateSupported = () => {
    let i = document.createElement("input")
    i.setAttribute("type", "date")
    return i.type !== "text"
  }

  let options

  if (selectedFilter.type === "multi_select") {
    options = selectedFilter.options.map((option) => ({
      label: option[1],
      value: option[0]
    }))
  }

  useEffect(() => {
    if (selectedFilter.type === "multi_select") {
      let values_array = q[selectedFilter.param] || []

      if (values_array.length > 0 && values_array[0].value === undefined) {
        values_array = values_array.map((value) => {
          const option = selectedFilter.options.find((option) => option[0] === value)
          return { value, label: option[1] || value }
        })
      }

      setMultiValue(values_array)
    }
  }, [selectedFilter])

  const filterComponent = () => {
    if (selectedFilter.type === "date") {
      return (
        <DefaultDateField
          key={selectedFilter.param}
          name={selectedFilter.param}
          value={q[selectedFilter.param]}
          placeholder={selectedFilter.name}
          handleInputChange={handleDateFieldChange}
        />
      )
    } else if (selectedFilter.type === "select") {
      return (
        <SelectField
          key={selectedFilter.param}
          name={selectedFilter.param}
          value={q[selectedFilter.param]}
          options={selectedFilter.options}
          placeholder={selectedFilter.name}
          handleInputChange={handleFilterChange}
          blank={selectedFilter.blank}
        />
      )
    } else if (selectedFilter.type === "number") {
      return (
        <NumberField
          key={selectedFilter.param}
          name={selectedFilter.param}
          value={q[selectedFilter.param]}
          handleInputChange={handleNumberFieldChange}
        />
      )
    } else if (selectedFilter.type === "multi_select") {
      return (
        <MultiSelectField
          key={selectedFilter.param}
          name={selectedFilter.param}
          value={multiValue}
          options={options}
          handleInputChange={handleMultiSelectChange}
        />
      )
    }
  }

  const checkSelected = ({ param, type }) => {
    const query = Object.entries(q).find((entry) => entry[0] === param)
    return query && query[1]
  }

  const anyFiltersSelected = () => {
    const filterNames = filters.map((filter) => filter.param)
    return Object.keys(q).some((param) => filterNames.includes(param))
  }

  return (
    <>
      {filters.length > 0 && (
        <Container>
          <P bold variant="secondary" marginless>
            Filters:
          </P>
          {filters.map((filter) => (
            <Button
              variant="secondary"
              outlined={!checkSelected(filter)}
              onClick={(event) => toggleFilter(event, filter)}
            >
              {filter.name}
            </Button>
          ))}
          {clearAllFiltersHandler !== undefined && anyFiltersSelected() && (
            <Button
              onClick={() => {
                clearAllFilters()
              }}
            >
              Clear Filters
            </Button>
          )}
        </Container>
      )}
      {!!showFilter && (
        <Grid template="auto / 1fr 1fr">
          <Card variant="secondary" useAlternativeVariant={true}>
            <StyledFieldGroup>
              <StyledLabel>
                {selectedFilter.name}{" "}
                {clearFilterHandler !== undefined && (
                  <CloseIcon onClick={(event) => handleClearFilter(selectedFilter.param)} />
                )}
              </StyledLabel>
              {filterComponent()}
              {isDateSupported() && selectedFilter.type === "date" && (
                <>
                  <Button onClick={() => saveDateChange(selectedFilter.param)}>Save</Button>
                </>
              )}
              {selectedFilter.type === "number" && (
                <>
                  <Button onClick={() => saveNumberChange(selectedFilter.param)}>Search</Button>
                </>
              )}
              {selectedFilter.type === "multi_select" && (
                <>
                  <Button onClick={() => saveMultiSelectChange(selectedFilter.param)}>Search</Button>
                </>
              )}
            </StyledFieldGroup>
          </Card>
        </Grid>
      )}
    </>
  )
}

Filters.propTypes = {
  filters: PropTypes.array,
  filterHandler: PropTypes.func,
  clearFilterHandler: PropTypes.func,
  clearAllFiltersHandler: PropTypes.func,
  q: PropTypes.object
}

Filters.defaultProps = {
  filters: [],
  filterHandler: undefined,
  clearFilterHandler: undefined,
  clearAllFiltersHandler: undefined,
  q: {}
}

export default Filters
